/***************************************************************************************
 * FileName      : EquipmentFooter.tsx
 * Description   : 하단 장비 선택 탭 바 (열교환기, 증류기, 순환기 + 가이드)
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.09
 * Modified Date : 2026.02.20
 * Reference     : mockup/index.html (Phase 3: 장비 선택 UI - Tab Footer)
 ***************************************************************************************/

import React from 'react';

// 2026.02.09 : 박은정 : 장비 탭 아이템 정의
interface EquipmentTab {
  id: string;
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
}

interface Props {
  isVisible: boolean;
  activeTab: string;
  onTabSelect: (tabId: string) => void;
  // 2026.02.09 : 박은정 : 장비별 학습 진행률(%) 맵 (localStorage에서 로드)
  equipmentProgress?: Record<string, number>;
}

// 2026.02.09 : 박은정 : 장비 탭 목록 (열교환기·증류기·순환기 + 가이드)
const EQUIPMENT_TABS: EquipmentTab[] = [
  {
    id: 'heat-exchanger',
    label: '열교환기',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M7 6v12M12 6v12M17 6v12" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
  },
  {
    id: 'distillation',
    label: '증류기',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 3h8M9 3v5l-5 8a2 2 0 002 2h12a2 2 0 002-2l-5-8V3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    id: 'circulation',
    label: '순환기',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
];

// 2026.02.09 : 박은정 : 유틸리티 탭 목록 (가이드 - 작동 X)
// 2025.01.27 : 박은정 : 학습현황 버튼 삭제
// 2026.02.20 : 박은정 : 기능이 없는 가이드 버튼 주석 처리
// const UTIL_TABS: EquipmentTab[] = [
//   {
//     id: 'guide',
//     label: '가이드',
//     disabled: true,
//     icon: (
//       <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
//         <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
//         <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
//       </svg>
//     ),
//   },
// ];

// 2026.02.09 : 박은정 : 하단 장비 선택 탭 바 컴포넌트 (진행률 뱃지 포함)
export const EquipmentFooter: React.FC<Props> = ({ isVisible, activeTab, onTabSelect, equipmentProgress = {} }) => {
  if (!isVisible) return null;

  return (
    <div className="eq-footer eq-footer--visible">
      <div className="eq-footer__tabs">
        {/* 장비 탭 (열교환기, 증류기, 순환기) + 진행률 뱃지 */}
        {EQUIPMENT_TABS.map((tab) => {
          const progress = equipmentProgress[tab.id] || 0;
          return (
            <button
              key={tab.id}
              className={`eq-footer__tab${activeTab === tab.id ? ' eq-footer__tab--active' : ''}`}
              onClick={() => onTabSelect(tab.id)}
            >
              <span className="eq-footer__tab-icon">{tab.icon}</span>
              <span className="eq-footer__tab-label">{tab.label}</span>
              {/* 2026.02.09 : 박은정 : 진행률이 0%보다 클 때만 뱃지 표시 */}
              {progress > 0 && (
                <span className={`eq-footer__tab-badge${progress >= 100 ? ' eq-footer__tab-badge--complete' : ' eq-footer__tab-badge--progress'}`}>
                  {progress >= 100 ? '완료' : `${progress}%`}
                </span>
              )}
            </button>
          );
        })}

        {/* 2026.02.20 : 박은정 : 기능이 없는 가이드 버튼 주석 처리 */}
        {/* <div className="eq-footer__divider" /> */}

        {/* {UTIL_TABS.map((tab) => (
          <button
            key={tab.id}
            className="eq-footer__tab eq-footer__tab--disabled"
            disabled
          >
            <span className="eq-footer__tab-icon">{tab.icon}</span>
            <span className="eq-footer__tab-label">{tab.label}</span>
          </button>
        ))} */}
      </div>
    </div>
  );
};

